'use client'

import { ButtonSecondary } from '@fukku/button/Secondary'
import { Select } from '@fukku/select'
import { IconCloseMedium } from 'icons/components/IconCloseMedium'
import { IconGeoS } from 'icons/components/IconGeoS'
import { IconSearchL } from 'icons/components/IconSearchL'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useFilteredStores } from 'maps/hooks/useFilteredStores/useFilteredStores'
import { useGeolocation } from 'maps/hooks/useGeolocation/useGeolocation'
import { useMapDispatch } from 'maps/hooks/useMapDispatch/useMapDispatch'
import { useMapState } from 'maps/hooks/useMapState/useMapState'
import { useStoredLocation } from 'maps/hooks/useStoredLocation/useStoredLocation'
import { geocode } from 'maps/services/client/geocode/geocode'
import { sendSearchStoreEvent } from 'maps/utils/analytics/events'
import { PLACEHOLDER_KEYS } from 'maps/utils/constants'
import { isDroppointType } from 'maps/utils/mapTypes/mapTypes'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useRef } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { cx } from 'utils/cx'

import text from 'fukku/styles/texts.module.scss'
import styles from './SearchLocationForm.module.scss'

export function SearchLocationForm(): React.ReactNode {
	const {
		country: { languageISO, countryISO },
	} = useMasterData()
	const { t } = useLabels()
	const { isLargeOrGreater } = useResponsive()

	const {
		isGeoLocationEnabled,
		geolocationErrorMessage,
		handleGetCurrentPosition,
	} = useGeolocation()
	const inputRef = useRef<HTMLInputElement | null>(null)
	const {
		searchInput,
		hasSearched,
		filteredStores,
		storesBrand,
		onModal,
		loading,
		type,
	} = useMapState()
	const dispatch = useMapDispatch()
	const { setLatestLocation } = useStoredLocation(true)
	const { handleFilteredStores, handleFilteredStoresByBrand } =
		useFilteredStores()
	const inputValue = useRef<string | undefined>(searchInput)

	const geolocationButtonRef = useRef<HTMLButtonElement | null>(null)

	const handleOnGeolocation = async (): Promise<void> => {
		geolocationButtonRef.current?.blur()
		try {
			await handleGetCurrentPosition()
		} catch {
			handleClear()
		}
	}

	const hasFilteredStores =
		!loading && filteredStores && filteredStores.length > 0

	const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		dispatch({ searchInput: event.target.value })
	}

	const handleOnChangeBrand = (value: string | number | null) => {
		if (typeof value === 'string') {
			dispatch({ selectedBrand: value })
			handleFilteredStoresByBrand(value)
		}
	}

	const handleSubmit = async (
		event: React.FormEvent<HTMLFormElement>
	): Promise<void> => {
		event.preventDefault()

		if (searchInput?.trim() && searchInput !== inputValue.current) {
			inputValue.current = searchInput
			inputRef.current?.blur()
			dispatch({ loading: true })
			const location = await geocode({
				address: searchInput,
				languageISO,
				countryISO,
			})

			if (location) {
				setLatestLocation(searchInput, location)
				dispatch({ location, loading: false })
			}

			sendSearchStoreEvent()
			handleFilteredStores(location)
		}
	}

	const handleClear = (): void => {
		dispatch({ searchInput: '' })
		inputRef.current?.focus()
	}

	const showClearOption = searchInput !== ''
	const noStoresFound = hasSearched && !filteredStores?.length
	const placeholderLabel = isDroppointType(type)
		? PLACEHOLDER_KEYS.DROPPOINT
		: PLACEHOLDER_KEYS.DEFAULT

	return (
		<form
			className={cx(
				styles.searchLocationForm,
				onModal && styles.modalWrapper,
				noStoresFound && styles.errorMessage
			)}
			onSubmit={handleSubmit}
			// biome-ignore lint/a11y/useSemanticElements: Fix this
			role='search'
		>
			<div className={cx(styles.wrapper, !onModal && styles.maxWidth)}>
				<div className={styles.searchbox}>
					<div className={styles.inputWrapper}>
						<span className={styles.icon}>
							<IconSearchL />
						</span>
						<input
							ref={inputRef}
							type='search'
							aria-labelledby='search-title'
							data-testid='maps.searchLocationForm.input'
							placeholder={t(placeholderLabel)}
							className={cx(text.inputText, styles.input)}
							onChange={handleOnChange}
							value={searchInput}
						/>
						{showClearOption && (
							<button
								aria-expanded
								aria-label={t('accessibility.icon.cross.delete.text')}
								className={styles.clearButton}
								onClick={handleClear}
								type='button'
							>
								<IconCloseMedium />
							</button>
						)}
					</div>
					<button
						ref={geolocationButtonRef}
						className={cx(text.bodyM, styles.geoButton)}
						data-testid='maps.searchLocationForm.geolocation'
						onClick={handleOnGeolocation}
						aria-disabled={!isGeoLocationEnabled}
						type='button'
						disabled={!isGeoLocationEnabled}
					>
						<div className={styles.geoText}>
							<span className={styles.icon}>
								<IconGeoS
									color={
										isGeoLocationEnabled ? 'content-1' : 'content-disabled'
									}
								/>
							</span>
							<p className={styles.optionText}>
								{isGeoLocationEnabled
									? t('storeLocator.searchBox.nearMe.placeholder')
									: geolocationErrorMessage}
							</p>
						</div>
					</button>
				</div>
				{isLargeOrGreater && (
					<ButtonSecondary
						type='submit'
						className={styles.button}
						data-testid='maps.searchLocationForm.submit'
					>
						{t('storeLocator.searchBox.search.button')}
					</ButtonSecondary>
				)}
			</div>
			{storesBrand && (
				<Select
					id='selectBrand'
					label={t('storeLocator.search.selector.all.option')}
					disabled={!hasFilteredStores}
					options={storesBrand}
					className={styles.selectBrand}
					onChange={handleOnChangeBrand}
				/>
			)}
		</form>
	)
}
